import { memo, useCallback, useEffect, useRef } from 'react'
import Fields from './Fields'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { isFieldActive, isFieldHidden, isReviewSwitchDisabled } from '../utils/fieldUtil'
import {
  ACTIVITY_TYPES,
  FIELD_TYPES,
  REVIEW_CONTEXTS,
  STANDARD_COLORS,
} from '../../../utility/constants'
import {
  useAllValues,
  useCommentValues,
  useFieldValidationError,
  useNoValidationValues,
  usePageDetails,
  usePreviousAllReviewData,
  usePreviousProcessData,
  useProcessContext,
  useReviewValues,
  useSelectedTableData,
} from '../../../store'
import WrapWithReviewSwitch from './WrapWithReviewSwitch'
import {
  getCommentId,
  getConstraintErrorId,
  getFieldId,
  getReviewId,
  getPageId,
  removeTableStructureData,
} from '../utils/utils'
import { processDataService } from '../../../services/processDataService'
import { autoComputeFieldValue } from '../utils/autoCompute'
import { useMediaQuery } from '@mui/material'
import { autoComputeFieldValueOptimized } from '../utils/autoComputeOptimized'

const Page = ({ page }) => {
  const { user } = useAuthContext()
  const conditionalEnableDisable = useProcessContext(
    (state) => state.processContext.conditionalEnableDisable,
  )

  /* center store values */
  const allValues = useAllValues((state) => state.allValues)
  const setAllValues = useAllValues((state) => state.setAllValues)

  /* review values */
  const reviewValues = useReviewValues((state) => state.reviewValues)
  const setReviewValues = useReviewValues((state) => state.setReviewValues)
  /* comment values */
  const commentValues = useCommentValues((state) => state.commentValues)

  // getting page fields
  const pageDetails = usePageDetails((state) => state.pageDetails)
  const fields = pageDetails[getPageId(page?.id)]
  fields?.sort((a, b) => a.sequence - b.sequence)

  // timer ref
  const saveReviewTimerRef = useRef()
  const saveCommentTimerRef = useRef()

  /* field validation error */
  const fieldValidationError = useFieldValidationError((state) => state.fieldValidationError)

  /* disabled field to skip validation */
  const setNoValidationValues = useNoValidationValues((state) => state.setNoValidationValues)
  const removeFromNoValidationValues = useNoValidationValues(
    (state) => state.removeFromNoValidationValues,
  )

  const listDataIdMetadataMap = useProcessContext(
    (state) => state.processContext.listDataIdMetadataMap,
  )

  const selectedTableData = useSelectedTableData((state) => state.selectedTableData)

  /*Previous Process Data*/
  const previousReviewProcessDataValues = usePreviousProcessData((state) =>
    state.previousProcessData.prevReviewProcessData
      ? state.previousProcessData.prevReviewProcessData
      : {},
  )

  /*Previous All Reviews*/
  const previousAllReviews = usePreviousAllReviewData((state) => state.previousAllReviews)

  /* process context */
  const processContext = useProcessContext((state) => state.processContext)
  const activityType = processContext?.activityTemplate?.activity_type
  const processStatus = processContext?.processData?.status
  const processModifiedBy = processContext?.processData?.modified_by
  const version = processContext?.processData?.version

  // timer ref
  const saveDataTimerRef = useRef()

  const triggerAutoCompute = async (allValues, listDataIdMetadataMap, processContext) => {
    const ctx = {
      processPrefix: processContext?.processTemplate?.prefix,
      processReferenceNum: processContext?.processData?.process_reference_num,
      processTemplateId: processContext?.processData?.process_template_id,
      version: processContext?.processData?.version,
    }

    const idFieldMap = {}
    processContext.allFields?.forEach((cur) => {
      idFieldMap[cur.id] = cur
    })

    const computedValues = await autoComputeFieldValueOptimized({
      allActivitiesCreatedAtWithVersion: processContext?.allActivitiesCreatedAtWithVersion,
      allProcessData: processContext?.allProcessData,
      context: ctx,
      dynamicListData: processContext?.dynamicListData,
      holidayList: processContext?.holidayList,
      listDataIdMetadataMap: listDataIdMetadataMap,
      selectedTableData: selectedTableData,
      tableRowIndex: null,
      tableSubfieldsMap: processContext?.tableSubfieldsMap,
      user: user,
      computeFields: processContext.allFields.filter((it) => it.is_computed && !it.is_subfield),
      data: { allValues, rowValues: {} },
      idFieldMap,
    })

    return computedValues
  }

  /* save all value change */
  useEffect(() => {
    if (
      processContext?.activityTemplate?.activity_type == ACTIVITY_TYPES.DATA_ENTRY ||
      (processContext?.activityTemplate?.activity_type == ACTIVITY_TYPES.LONG_RUNNING &&
        user.id == processContext?.processData?.modified_by)
    ) {
      if (saveDataTimerRef.current) {
        clearTimeout(saveDataTimerRef.current)
      }
      const values = removeTableStructureData(allValues, processContext?.allFields)

      saveDataTimerRef.current = setTimeout(() => {
        ;(async () => {
          const modifiedByID =
            processContext?.activityTemplate?.activity_type === REVIEW_CONTEXTS.LONG_RUNNING
              ? processModifiedBy
              : user?.id
          processDataService.saveProcessData(
            user,
            processContext?.processData?.id,
            values,
            modifiedByID,
          )
        })()
        saveDataTimerRef.current = null
      }, 3000)
    }
  }, [JSON.stringify(allValues)])

  useEffect(() => {
    if (
      activityType !== ACTIVITY_TYPES.REVIEW &&
      allValues &&
      listDataIdMetadataMap &&
      processContext
    ) {
      ;(async () => {
        const computedValues = await triggerAutoCompute(
          allValues,
          listDataIdMetadataMap,
          processContext,
        )
        setAllValues({ ...allValues, ...computedValues })
      })()
    }
  }, [processContext])

  useEffect(() => {
    if (
      processContext?.activityTemplate?.activity_type === REVIEW_CONTEXTS.REVIEW ||
      (processContext?.activityTemplate?.activity_type === ACTIVITY_TYPES.LONG_RUNNING &&
        user.id !== processContext?.processData?.modified_by)
    ) {
      if (saveCommentTimerRef.current) {
        clearTimeout(saveCommentTimerRef.current)
      }
      const values = removeTableStructureData(commentValues, processContext?.allFields)

      saveCommentTimerRef.current = setTimeout(() => {
        ;(async () => {
          const modifiedByID =
            processContext?.activityTemplate?.activity_type === REVIEW_CONTEXTS.LONG_RUNNING
              ? processModifiedBy
              : user.id
          processDataService.saveProcessCommentData(
            user,
            processContext?.processData?.id,
            values,
            modifiedByID,
          )
        })()
      }, 3000)
    }
  }, [commentValues])

  // Removed checking if user is reviewer or data-entry-guy/gal
  // To change review status to null of corresponding field changes

  useEffect(() => {
    if (
      processContext?.activityTemplate?.activity_type === REVIEW_CONTEXTS.REVIEW ||
      processContext?.activityTemplate?.activity_type === ACTIVITY_TYPES.LONG_RUNNING // Important:  && user.id != processContext?.processData?.modified_by
    ) {
      if (saveReviewTimerRef.current) {
        clearTimeout(saveReviewTimerRef.current)
      }
      const values = removeTableStructureData(reviewValues, processContext?.allFields)

      saveReviewTimerRef.current = setTimeout(() => {
        ;(async () => {
          const modifiedByID =
            processContext?.activityTemplate?.activity_type === REVIEW_CONTEXTS.LONG_RUNNING
              ? processModifiedBy
              : user.id
          processDataService.saveProcessReviewData(
            user,
            processContext?.processData?.id,
            values,
            modifiedByID,
          )
        })()
      }, 3000)
    }
  }, [reviewValues])

  const isReviewDisabled = useCallback(() => {
    return isReviewSwitchDisabled(activityType, processStatus, processModifiedBy, user?.id)
  }, [activityType, activityType, processModifiedBy, user?.id])

  const isSubsiqentDataEntry = () => {
    return activityType == ACTIVITY_TYPES.DATA_ENTRY && version > 1
  }

  // Get Review Status for a field (true/false)
  const getEffectiveReviewForField = (field) => {
    // Prefer live review state if present
    const live = reviewValues[getReviewId(field.id)]
    if (live !== undefined) return live

    if (isSubsiqentDataEntry()) {
      if (
        Object.keys(previousReviewProcessDataValues || {}).length > 0 &&
        Object.keys(previousReviewProcessDataValues?.review_data || {}).includes(
          getReviewId(field.id),
        )
      ) {
        return previousReviewProcessDataValues?.review_data[getReviewId(field.id)]
      }
      return undefined
    }
    return live
  }
  const isFieldDisabled = (field) => {
    const isDisabled = !isFieldActive(
      field,
      allValues,
      conditionalEnableDisable,
      activityType,
      processStatus,
      version,
      previousReviewProcessDataValues?.review_data,
      reviewValues,
      processModifiedBy,
      user?.id,
    )

    if (activityType != ACTIVITY_TYPES.REVIEW) {
      if (isDisabled) {
        setNoValidationValues(getFieldId(field.id))
      } else {
        removeFromNoValidationValues(getFieldId(field.id))
      }
    }
    return isDisabled
  }

  const isHidden = (field) => {
    const isHidden = isFieldHidden(field, processContext?.hiddenFields, [], {
      allValues,
      conditionalEnableDisable,
    })

    if (isHidden) {
      if (field.data_structure !== FIELD_TYPES.TABLE) {
        setNoValidationValues(getFieldId(field.id))
      } else {
        setNoValidationValues(getFieldId(field.id))
        let tableSubfields = processContext?.tableSubfields
          ?.find((it) => it.id == field.id)
          ?.subfields?.map((subField) => subField.id + '')
        Object.keys(allValues).forEach((key) => {
          let fieldId = key.split('__')[0].split('_')[1]
          if (tableSubfields.includes(fieldId)) {
            setNoValidationValues(key)
          }
        })
      }
    } else {
      if (field.data_structure !== FIELD_TYPES.TABLE) {
        removeFromNoValidationValues(getFieldId(field.id))
      } else {
        removeFromNoValidationValues(getFieldId(field.id))
        let tableSubfields = processContext?.tableSubfields
          ?.find((it) => it.id == field.id)
          ?.subfields?.map((subField) => subField.id + '')
        Object.keys(allValues).forEach((key) => {
          let fieldId = key.split('__')[0].split('_')[1]
          if (tableSubfields.includes(fieldId)) {
            removeFromNoValidationValues(getFieldId(field.id))
          }
        })
      }
    }
    return isHidden
  }
  // render field
  const renderField = (field, colSpanUi) => {
    const getFieldStyle = () => {
      const disabled = isFieldDisabled(field)
      const rvw = getEffectiveReviewForField(field)
      if (rvw === true)
        return {
          backgroundColor: STANDARD_COLORS.BG_READONLY,
          color: STANDARD_COLORS.TEXT_ACCEPTED,
        }
      if (rvw === false)
        return {
          backgroundColor: STANDARD_COLORS.BG_EDITABLE,
          color: STANDARD_COLORS.TEXT_REJECTED,
        }
      if (disabled)
        return { backgroundColor: STANDARD_COLORS.BG_READONLY, color: STANDARD_COLORS.TEXT_DEFAULT }

      return { backgroundColor: STANDARD_COLORS.BG_EDITABLE, color: STANDARD_COLORS.TEXT_DEFAULT }
    }
    const fieldStyle = getFieldStyle()
    return (
      <>
        {!isHidden(field) &&
          (field.data_structure !== FIELD_TYPES.TABLE ? (
            <div
              key={field.id}
              style={{
                gridColumn: [FIELD_TYPES.TABLE, FIELD_TYPES.CUSTOM_VIEW].includes(
                  field.data_structure,
                )
                  ? 'span 4'
                  : `span ${colSpanUi}`,
              }}
            >
              <WrapWithReviewSwitch
                field={field}
                review={
                  isSubsiqentDataEntry()
                    ? Object.keys(previousReviewProcessDataValues)?.length > 0 &&
                      Object.keys(previousReviewProcessDataValues?.review_data).includes(
                        getReviewId(field.id),
                      )
                      ? previousReviewProcessDataValues?.review_data[getReviewId(field?.id)]
                      : true
                    : reviewValues[getReviewId(field?.id)]
                }
                comment={
                  isSubsiqentDataEntry()
                    ? Object.keys(previousReviewProcessDataValues).length > 0 &&
                      Object.keys(previousReviewProcessDataValues?.review_comments_data).includes(
                        getCommentId(field.id),
                      )
                      ? previousReviewProcessDataValues?.review_comments_data[
                          getCommentId(field.id)
                        ]
                      : ''
                    : commentValues[getCommentId(field.id)]
                }
                previousReviews={
                  previousAllReviews ? previousAllReviews[getReviewId(field.id)] : []
                }
                onReviewChange={(cmt) =>
                  setReviewValues({
                    ...reviewValues,
                    [getReviewId(field.id)]: cmt,
                  })
                }
                disabled={isReviewDisabled()}
                fields={fields}
              >
                <div
                  style={{
                    backgroundColor: 'transparent',
                  }}
                >
                  <Fields
                    field={field}
                    fields={fields}
                    disabled={isFieldDisabled(field)}
                    helperText={fieldValidationError[getConstraintErrorId(field.id)]}
                    inputSx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: fieldStyle.backgroundColor,
                        color: fieldStyle.color,
                      },
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: fieldStyle.color,
                        color: fieldStyle.color,
                      },
                    }}
                  />
                </div>
              </WrapWithReviewSwitch>
            </div>
          ) : (
            <div
              key={field.id}
              style={{
                gridColumn: [FIELD_TYPES.TABLE, FIELD_TYPES.CUSTOM_VIEW].includes(
                  field.data_structure,
                )
                  ? 'span 4'
                  : `span ${colSpanUi}`,
              }}
            >
              <Fields
                field={field}
                fields={fields}
                disabled={isFieldDisabled(field)}
                updatedAt={allValues.updatedAt}
                inputSx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: fieldStyle.backgroundColor,
                    color: fieldStyle.color,
                  },
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: fieldStyle.color,
                    color: fieldStyle.color,
                  },
                }}
              />
            </div>
          ))}
      </>
    )
  }

  const TOTAL_COLUMN_SPAN = 4
  let remainingSpan = TOTAL_COLUMN_SPAN // Tracks available space in the current row
  const isSmallScreen = useMediaQuery('(max-width:910px)')

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(4, 1fr)`,
          gap: '10px',
          marginBlock: '13px',
        }}
      >
        {fields
          ?.filter((it) => !it.is_subfield)
          ?.map((field) => {
            // Check if a new row needs to be started
            const colSpanUi = isSmallScreen ? TOTAL_COLUMN_SPAN : field.colspan_ui
            if (
              field.is_start_on_new_line_ui &&
              remainingSpan > 0 &&
              remainingSpan < TOTAL_COLUMN_SPAN
            ) {
              const spacer = (
                <div
                  key={`spacer-${field.id}`}
                  style={{
                    gridColumn: `span ${remainingSpan}`,
                  }}
                ></div>
              )

              // Decrease remainingSpan for the current field
              const currentFieldSpan = colSpanUi || 1
              remainingSpan = TOTAL_COLUMN_SPAN - currentFieldSpan

              // If the row is full, reset remainingSpan
              if (remainingSpan <= 0) {
                remainingSpan = TOTAL_COLUMN_SPAN
              }

              return (
                <>
                  {spacer}
                  {renderField(field, colSpanUi)}
                </>
              )
            }

            // Decrease remainingSpan for the current field
            const currentFieldSpan = colSpanUi || 1
            remainingSpan -= currentFieldSpan

            // If the row is full, reset remainingSpan
            if (remainingSpan <= 0) {
              remainingSpan = TOTAL_COLUMN_SPAN
            }

            return renderField(field, colSpanUi)
          })}
      </div>
    </>
  )
}

export default memo(Page, (prevProps, nextProps) => prevProps.page === nextProps.page)
